import { rgba } from 'polished';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { tokens } from '../theme/tokens';
import { Skeleton } from './Skeleton';

const Container = styled(Box)`
	position: relative;
`;

const Svg = styled.svg`
	display: block;
`;

const Circle = styled.circle<{ skeleton?: boolean }>`
	stroke: ${(props) => (props.skeleton ? props.theme.colors.lightBlueDark : tokens.border.subtle)};
	fill: transparent;
`;

const Tick = styled.text`
	fill: ${(props) => props.theme.colors.darkGray};
	font-weight: bold;
	font-size: 12px;
	text-anchor: middle;
`;

const Region = styled.path<{ skeleton?: boolean; color?: string }>`
	stroke: ${(props) => props.color ?? props.theme.colors.primary};
	stroke-width: 2px;
	fill: ${(props) => rgba(props.color ?? props.theme.colors.primary, 0.25)};

	${(props) =>
		props.skeleton &&
		`
		stroke: none;
		fill: ${rgba(props.theme.colors.lightBlueDark, 0.5)};
		transition: 1s;
	`}
`;

const Label = styled(Box)<{ x: number; y: number }>`
	position: absolute;
	left: ${(props) => props.x}px;
	top: ${(props) => props.y}px;
`;

function computeRegion(scores: number[]): [number, number][] {
	const angle = (Math.PI / scores.length) * 2;

	return scores.map((score, i) => [
		(Math.sin(angle * (i + 2)) * score) / 5,
		(Math.cos(angle * (i + 2)) * score) / 5,
	]);
}

export function RadarChart(props: {
	labels: string[];
	scores: number[];
	expectedScores?: number[];
	expectedColor: string;
	currentColor: string;
}) {
	const maxScore = Math.ceil(Math.max(...props.scores));
	const size = 240;
	const path = computeRegion(props.scores);
	const expectedPath = props.expectedScores ? computeRegion(props.expectedScores) : null;
	const maxRange = new Array(maxScore).fill(null).map((_, i) => i + 1);

	return (
		<Flex py={4} justifyContent="center">
			<Container>
				<Svg width={size} height={size}>
					{maxRange.map((i) => (
						<Circle key={i} cx={size / 2} cy={size / 2} r={(size / 2 / 5) * i - 1} />
					))}
					{expectedPath && (
						<Region
							color={props.expectedColor}
							d={`M ${expectedPath[expectedPath.length - 1][0] * (size / 2 - 2) + size / 2} ${
								expectedPath[expectedPath.length - 1][1] * (size / 2 - 2) + size / 2
							} ${expectedPath.map(
								([x, y]) => `L ${x * (size / 2 - 2) + size / 2} ${y * (size / 2 - 2) + size / 2}`,
							)}`}
						/>
					)}
					<Region
						color={props.currentColor}
						d={`M ${path[path.length - 1][0] * (size / 2 - 2) + size / 2} ${
							path[path.length - 1][1] * (size / 2 - 2) + size / 2
						} ${path.map(
							([x, y]) => `L ${x * (size / 2 - 2) + size / 2} ${y * (size / 2 - 2) + size / 2}`,
						)}`}
					/>
					{maxRange.map((i) => (
						<Tick key={i} x={size / 2 + (size / 2 / 5) * (i - 0.5) + 10} y={size / 2 + 3}>
							{i}
						</Tick>
					))}
				</Svg>
				{props.labels.map((label, i) => {
					const radius = 48 * maxScore;
					const angle = (Math.PI / props.labels.length) * 2;
					const x = Math.sin(angle * (i + 2)) * (radius / 2 + 10);
					const y = Math.cos(angle * (i + 2)) * (radius / 2 + 10);

					return (
						<Label
							key={i}
							x={x + size / 2}
							y={y + size / 2}
							style={{
								color: 'gray',
								fontSize: '11px',
								transform: [
									`translateX(${Math.round(x) === 0 ? '-50%' : x < 0 ? '-100%' : '0%'})`,
									`translateY(${Math.round(y) === 0 ? '-50%' : y < 0 ? '-100%' : '0%'})`,
								].join(' '),
							}}
						>
							{label}
						</Label>
					);
				})}
			</Container>
		</Flex>
	);
}

function RadarChartSkeleton() {
	const size = 160;

	const count = 4;

	const [toggle, setToggle] = useState(false);

	const path = useMemo(
		() => computeRegion([...new Array(count)].map(() => Math.round(Math.random() * 4 + 1))),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[toggle],
	);

	useEffect(() => {
		setToggle((toggle) => !toggle);

		const interval = setInterval(() => setToggle((toggle) => !toggle), 1000);
		return () => clearInterval(interval);
	}, []);

	return (
		<Flex py={4} justifyContent="center">
			<Container>
				<Svg width={size} height={size}>
					{[1, 2, 3, 4, 5].map((i) => (
						<Fragment key={i}>
							<Circle cx={size / 2} cy={size / 2} r={(size / 2 / 5) * i - 1} skeleton />
						</Fragment>
					))}
					<Region
						skeleton
						d={`M ${path[path.length - 1][0] * (size / 2 - 2) + size / 2} ${
							path[path.length - 1][1] * (size / 2 - 2) + size / 2
						} ${path.map(
							([x, y]) => `L ${x * (size / 2 - 2) + size / 2} ${y * (size / 2 - 2) + size / 2}`,
						)}`}
					/>
				</Svg>
				{[...new Array(count)].map((_, i) => {
					const angle = (Math.PI / count) * 2;
					const x = Math.sin(angle * (i + 2)) * (size / 2 + 10);
					const y = Math.cos(angle * (i + 2)) * (size / 2 + 10);

					return (
						<Label
							key={i}
							x={x + size / 2}
							y={y + size / 2}
							style={{
								transform: [
									`translateX(${Math.round(x) === 0 ? '-50%' : x < 0 ? '-100%' : '0%'})`,
									`translateY(${Math.round(y) === 0 ? '-50%' : y < 0 ? '-100%' : '0%'})`,
								].join(' '),
							}}
						>
							<Skeleton.Box width={40} height={20} />
						</Label>
					);
				})}
			</Container>
		</Flex>
	);
}

RadarChart.Skeleton = RadarChartSkeleton;
